import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'

import Langs, { Localize } from '../../../app/lang/langs'
import { getVLearnAttemptReview } from '../../../containers/vlearn/vlearnSlice'
import { vlearnSurface, vlearnType, VLearnEmptyState } from '../vlearnUi'

/**
 * V-Learning — the per-question correction.
 *
 * ── Only fields the controller actually sends ───────────────────────────────────────
 *
 * V-Sécurité's review reads `reviewData?.quiz?.allow_scores`, a field its backend never
 * puts in the response, so the flag is permanently falsy and the screen has been quietly
 * degraded since it shipped. Everything read here comes from `getAttemptReview`'s own
 * response body: `attempt` (with the score fields present only when `show_score` allows
 * them), `quiz` as `{ id, title, passing_percent, partial_credit }` — no reveal flag lives
 * there — and `questions` with `answered`, `is_correct`, `points_awarded`,
 * `points_possible`, `explanation` and the option rows carrying `is_correct`,
 * `was_selected` and `active`.
 *
 * ── A 403 is a policy answer, not a failure ─────────────────────────────────────────
 *
 * `show_answers` is enforced at the endpoint with a 403 rather than by hiding a button,
 * because a hidden button leaves the endpoint open to anyone reading the network tab. When
 * that 403 comes back it means the teacher chose not to publish corrections for this
 * attempt — which is an explanation the learner deserves in the page, not an error toast
 * that reads like the site broke.
 *
 * @param {object} props
 * @param {number} props.idCourse
 * @param {number} props.idAttempt the attempt to open first
 * @param {Function} props.onBack
 */
export default function QuizReview({ idCourse, idAttempt, onBack }) {
    const dispatch = useDispatch()
    const lang = useSelector((state) => state.settings.lang)

    const review = useSelector((state) => state.vlearn.attemptReview)
    const myAttempts = useSelector((state) => state.vlearn.myAttempts)

    /**
     * Which attempt is on screen.
     *
     * Seeded from the prop, because the learner asked for that one. The "best attempt"
     * default that V-Sécurité applies is used only when no attempt was named — see
     * `bestAttemptId` below.
     */
    const [selectedId, setSelectedId] = useState(idAttempt || null)
    const [loading, setLoading] = useState(true)
    const [forbidden, setForbidden] = useState(false)

    /**
     * Only attempts the server says are reviewable are offered in the switcher.
     *
     * `canReview` per row comes from `mayRevealAnswers`, so under an `after_pass` policy the
     * list is exactly the passed attempts — offering the others would be offering a 403.
     */
    const reviewable = useMemo(
        () => (Array.isArray(myAttempts) ? myAttempts.filter((attempt) => attempt.canReview) : []),
        [myAttempts])

    const bestAttemptId = useMemo(() => {
        if (!reviewable.length) return null
        // Passed first, then the highest percentage. `percent` may be absent when scores are
        // withheld, in which case the sort degrades to "any passed attempt, else the first"
        // rather than throwing on undefined.
        const sorted = [...reviewable].sort((a, b) => {
            const passedDelta = Number(b.passed === true) - Number(a.passed === true)
            if (passedDelta !== 0) return passedDelta
            return (Number(b.percent) || 0) - (Number(a.percent) || 0)
        })
        return sorted[0].id
    }, [reviewable])

    useEffect(() => {
        if (selectedId || !bestAttemptId) return
        setSelectedId(bestAttemptId)
    }, [bestAttemptId, selectedId])

    useEffect(() => {
        if (!idCourse || !selectedId) return undefined
        let cancelled = false
        setLoading(true)
        setForbidden(false)

        dispatch(getVLearnAttemptReview({ id_course: idCourse, id_attempt: selectedId }))
            .then((action) => {
                if (cancelled) return
                const error = action && action.payload && action.payload.responseError
                setForbidden(!!error && Number(error.status) === 403)
                setLoading(false)
            })

        return () => { cancelled = true }
    }, [dispatch, idCourse, selectedId])

    const handleSwitch = useCallback((event) => {
        setSelectedId(Number(event.target.value))
    }, [])

    const attempt = review && review.attempt ? review.attempt : null
    const questions = review && Array.isArray(review.questions) ? review.questions : []
    const hasScore = attempt && typeof attempt.percent === 'number'
    const showsScoresInSwitcher = reviewable.some((row) => typeof row.percent === 'number')

    const header = (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ mb: 2.5 }}
        >
            <Button
                variant="text"
                onClick={onBack}
                startIcon={<ArrowBackRoundedIcon />}
                sx={quietButtonSx}
            >
                <Langs str="VLEARN_REVIEW_BTN_BACK" />
            </Button>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography component="h2" sx={{ ...vlearnType.sectionTitle }}>
                    <Langs str="VLEARN_REVIEW_TITLE" />
                </Typography>
                {review && review.quiz && review.quiz.title ? (
                    <Typography sx={{ ...vlearnType.meta }}>{review.quiz.title}</Typography>
                ) : null}
            </Box>

            {/* The switcher only earns its space when there is something to switch between. */}
            {reviewable.length > 1 ? (
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                    <InputLabel id="vlearn-review-attempt-label" sx={{ fontSize: '0.85rem' }}>
                        <Langs str="VLEARN_REVIEW_ATTEMPT_LABEL" />
                    </InputLabel>
                    <Select
                        labelId="vlearn-review-attempt-label"
                        label={Localize('VLEARN_REVIEW_ATTEMPT_LABEL', lang)}
                        value={selectedId || ''}
                        onChange={handleSwitch}
                        sx={{ borderRadius: '11px', fontSize: '0.85rem', fontWeight: 600 }}
                    >
                        {reviewable.map((row) => (
                            <MenuItem key={row.id} value={row.id} sx={{ fontSize: '0.85rem', gap: 1 }}>
                                <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>{`#${row.attempt_no}`}</Box>
                                <Box component="span" sx={{ color: 'var(--text-secondary, #4b5263)', flex: 1 }}>
                                    {row.submitted_at ? moment(row.submitted_at).format('DD/MM/YYYY') : '—'}
                                </Box>
                                {showsScoresInSwitcher && typeof row.percent === 'number' ? (
                                    <Box component="span" sx={{
                                        fontWeight: 700,
                                        color: row.passed === true ? '#1F7A4C' : '#B3271F'
                                    }}>
                                        {`${Math.round(row.percent)}%`}
                                    </Box>
                                ) : null}
                                {row.id === bestAttemptId ? (
                                    <Box
                                        component="span"
                                        aria-label={Localize('VLEARN_REVIEW_BEST_ARIA', lang)}
                                        sx={{ color: 'var(--orange, rgb(255,144,21))', fontWeight: 700 }}
                                    >
                                        ★
                                    </Box>
                                ) : null}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            ) : null}
        </Stack>
    )

    if (forbidden) {
        return (
            <Box component="section">
                {header}
                <Box sx={{ ...vlearnSurface.card }}>
                    <VLearnEmptyState
                        icon={<LockOutlinedIcon />}
                        title={<Langs str="VLEARN_REVIEW_FORBIDDEN_TITLE" />}
                        description={<Langs str="VLEARN_REVIEW_FORBIDDEN_BODY" />}
                    />
                </Box>
            </Box>
        )
    }

    if (loading && !review) {
        return (
            <Box component="section">
                {header}
                <Box sx={{ ...vlearnSurface.card, display: 'flex', justifyContent: 'center', padding: '56px' }}>
                    <CircularProgress size={38} sx={{ color: 'var(--orange, rgb(255,144,21))' }} />
                </Box>
            </Box>
        )
    }

    if (!review || !questions.length) {
        return (
            <Box component="section">
                {header}
                <Box sx={{ ...vlearnSurface.card }}>
                    <VLearnEmptyState
                        title={<Langs str="VLEARN_REVIEW_EMPTY_TITLE" />}
                        description={<Langs str="VLEARN_REVIEW_EMPTY_BODY" />}
                    />
                </Box>
            </Box>
        )
    }

    return (
        <Box component="section" aria-labelledby="vlearn-review-heading">
            {header}

            <Box sx={{ ...vlearnSurface.card, padding: { xs: '18px 16px', sm: '22px 24px' }, mb: 2.5 }}>
                <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1.5 }}>
                    <Metric
                        label={<Langs str="VLEARN_REVIEW_METRIC_ATTEMPT" />}
                        value={`#${attempt ? attempt.attempt_no : '—'}`}
                    />
                    <Metric
                        label={<Langs str="VLEARN_REVIEW_METRIC_SUBMITTED" />}
                        value={attempt && attempt.submitted_at
                            ? moment(attempt.submitted_at).format('DD/MM/YYYY HH:mm') : '—'}
                    />
                    {/* Absent when `show_score` withholds them — the same rule as the result
                        screen, and for the same reason. */}
                    {hasScore ? (
                        <Metric
                            label={<Langs str="VLEARN_REVIEW_METRIC_SCORE" />}
                            value={`${Math.round(attempt.percent)}%`}
                            color={attempt.passed === true ? '#1F7A4C' : '#B3271F'}
                        />
                    ) : null}
                    {attempt && typeof attempt.score_points === 'number' && typeof attempt.max_points === 'number' ? (
                        <Metric
                            label={<Langs str="VLEARN_REVIEW_METRIC_POINTS" />}
                            value={`${formatPoints(attempt.score_points)} / ${formatPoints(attempt.max_points)}`}
                        />
                    ) : null}
                    {review.quiz && review.quiz.partial_credit ? (
                        <Metric
                            label={<Langs str="VLEARN_REVIEW_METRIC_GRADING" />}
                            value={Localize('VLEARN_REVIEW_GRADING_PARTIAL', lang)}
                        />
                    ) : null}
                </Stack>
            </Box>

            <Typography id="vlearn-review-heading" component="h3" sx={{ ...vlearnType.sectionTitle, mb: 1.5 }}>
                <Langs str="VLEARN_REVIEW_QUESTIONS_HEADING" />
            </Typography>

            <Stack spacing={2}>
                {questions.map((question, index) => (
                    <ReviewQuestion key={question.id} index={index} question={question} lang={lang} />
                ))}
            </Stack>
        </Box>
    )
}

// ── One corrected question ──────────────────────────────────────────────────────────

function ReviewQuestion({ index, question, lang }) {
    const correct = !!question.is_correct
    const answered = !!question.answered

    const accent = correct
        ? { bg: 'rgba(58,156,96,0.09)', border: 'rgba(58,156,96,0.24)', fg: '#1F7A4C' }
        : { bg: 'rgba(251,105,98,0.09)', border: 'rgba(251,105,98,0.24)', fg: '#B3271F' }
    const HeaderIcon = correct ? CheckCircleRoundedIcon : CancelRoundedIcon

    return (
        <Box sx={{ ...vlearnSurface.card }}>
            <Box sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1.5,
                px: { xs: 2, sm: 2.5 }, py: 1.75,
                backgroundColor: accent.bg,
                borderBottom: `1px solid ${accent.border}`
            }}>
                <HeaderIcon aria-hidden="true" sx={{ color: accent.fg, fontSize: 20, flexShrink: 0, mt: '2px' }} />
                <Typography component="h4" sx={{
                    flex: 1, fontSize: '0.92rem', fontWeight: 600, lineHeight: 1.45,
                    color: 'var(--text-primary, #111318)'
                }}>
                    <Box component="span" sx={{ color: 'var(--text-muted, #8b92a0)', mr: 1 }}>{`${index + 1}.`}</Box>
                    {question.question_text}
                </Typography>
                <Chip
                    size="small"
                    label={`${formatPoints(question.points_awarded)} / ${formatPoints(question.points_possible)}`}
                    aria-label={Localize('VLEARN_REVIEW_POINTS_ARIA', lang)
                        .replace('%awarded%', formatPoints(question.points_awarded))
                        .replace('%possible%', formatPoints(question.points_possible))}
                    sx={{
                        flexShrink: 0, height: 24, borderRadius: '7px', fontWeight: 700, fontSize: '0.72rem',
                        backgroundColor: '#fff', color: accent.fg, border: `1px solid ${accent.border}`
                    }}
                />
            </Box>

            {!answered ? (
                <Typography sx={{ ...vlearnType.meta, px: { xs: 2, sm: 2.5 }, pt: 1.5, fontWeight: 600 }}>
                    <Langs str="VLEARN_REVIEW_NOT_ANSWERED" />
                </Typography>
            ) : null}

            <Stack spacing={0.75} sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
                {(question.options || []).map((option) => (
                    <OptionRow key={option.id} option={option} lang={lang} />
                ))}
            </Stack>

            {question.explanation ? (
                <Box sx={{
                    mx: { xs: 2, sm: 2.5 }, mb: 2.25, px: 2, py: 1.5, borderRadius: '11px',
                    display: 'flex', gap: 1.25, alignItems: 'flex-start',
                    backgroundColor: 'rgba(255,144,21,0.07)', border: '1px solid rgba(255,144,21,0.24)'
                }}>
                    <LightbulbOutlinedIcon aria-hidden="true" sx={{ color: 'var(--orange, rgb(255,144,21))', fontSize: 18, mt: '2px', flexShrink: 0 }} />
                    <Typography sx={{ ...vlearnType.body }}>
                        <Box component="span" sx={{ fontWeight: 700, color: 'var(--text-primary, #111318)', mr: 0.5 }}>
                            {`${Localize('VLEARN_REVIEW_EXPLANATION_LABEL', lang)} :`}
                        </Box>
                        {question.explanation}
                    </Typography>
                </Box>
            ) : null}
        </Box>
    )
}

/**
 * One option of a corrected question.
 *
 * Four states, and each carries an icon as well as a colour so the correction is readable
 * without colour vision: the right answer taken, the right answer missed, a wrong answer
 * taken, and an untouched distractor. The label spells the state out in words too, because
 * "green with a ring" is not something a learner should have to decode.
 */
function OptionRow({ option, lang }) {
    const correct = !!option.is_correct
    const selected = !!option.was_selected

    let style
    let Icon
    let stateKey = null
    if (correct && selected) {
        style = { bg: 'rgba(58,156,96,0.09)', border: 'rgba(58,156,96,0.28)', fg: '#1F7A4C' }
        Icon = CheckCircleRoundedIcon
        stateKey = 'VLEARN_REVIEW_OPTION_CORRECT_CHOSEN'
    } else if (correct && !selected) {
        style = { bg: 'rgba(58,156,96,0.06)', border: 'rgba(58,156,96,0.24)', fg: '#1F7A4C' }
        Icon = CheckCircleOutlineIcon
        stateKey = 'VLEARN_REVIEW_OPTION_CORRECT_MISSED'
    } else if (!correct && selected) {
        style = { bg: 'rgba(251,105,98,0.09)', border: 'rgba(251,105,98,0.28)', fg: '#B3271F' }
        Icon = CancelRoundedIcon
        stateKey = 'VLEARN_REVIEW_OPTION_WRONG_CHOSEN'
    } else {
        style = { bg: 'transparent', border: 'rgba(0,0,0,0.08)', fg: 'var(--text-muted, #8b92a0)' }
        Icon = RadioButtonUncheckedIcon
    }

    return (
        <Box sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1.25, px: 1.5, py: 1,
            borderRadius: '10px', backgroundColor: style.bg, border: `1px solid ${style.border}`
        }}>
            <Icon aria-hidden="true" sx={{ color: style.fg, fontSize: 18, flexShrink: 0, mt: '2px' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                    fontSize: '0.86rem',
                    fontWeight: correct || selected ? 600 : 400,
                    color: correct || selected ? style.fg : 'var(--text-secondary, #4b5263)'
                }}>
                    {option.option_text}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    {stateKey ? (
                        <Typography sx={{ ...vlearnType.meta, color: style.fg }}>
                            <Langs str={stateKey} />
                        </Typography>
                    ) : null}
                    {/* The review deliberately keeps retired options the learner actually
                        selected, because option ids are stable on this backend and last
                        month's paper still resolves. Flagging them stops the learner
                        wondering why the choice is gone from the current quiz. */}
                    {option.active === false ? (
                        <Typography sx={{ ...vlearnType.meta }}>
                            {`· ${Localize('VLEARN_REVIEW_OPTION_RETIRED', lang)}`}
                        </Typography>
                    ) : null}
                </Stack>
            </Box>
        </Box>
    )
}

function Metric({ label, value, color }) {
    return (
        <Box>
            <Typography sx={{ ...vlearnType.meta, textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 600 }}>
                {label}
            </Typography>
            <Typography sx={{
                fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2,
                color: color || 'var(--text-primary, #111318)'
            }}>
                {value}
            </Typography>
        </Box>
    )
}

function formatPoints(value) {
    const number = Number(value)
    if (!Number.isFinite(number)) return '0'
    return Number.isInteger(number) ? String(number) : number.toFixed(2).replace(/0$/, '')
}

const quietButtonSx = {
    borderRadius: '12px',
    minHeight: 44,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.88rem',
    color: 'var(--text-secondary, #4b5263)',
    alignSelf: 'flex-start',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
    '&:focus-visible': { outline: '3px solid rgba(255,144,21,0.45)', outlineOffset: '2px' }
}
